/**
 * Presigned URLs, fetched in chunks as the upload queue reaches them.
 *
 * The batch used to ask for every URL in ONE request with no retry. For a
 * 1,000-photo order that single call was a single point of failure: one
 * transient 502 and the whole batch failed before a byte moved. For 6,000
 * photos it was also an ~8 MB response, and the URLs were minted hours before
 * the last of them was used.
 *
 * Now each chunk is requested only when the PUT queue gets close to it, and a
 * failed request is retried with backoff. A chunk whose retries all fail is
 * asked again, after a pause, whenever its files are reached -- for as long as
 * the upload gate would sit out a storage outage (MAX_STALL_MS), counted from
 * the moment its files first needed it. A short API outage therefore delays a
 * chunk instead of losing all of its files; only a definitive refusal (4xx) or
 * an outage longer than that costs them.
 *
 * Lazy also matters to the server: every presigned file becomes an
 * upload_intent row that the sweep keeps checking for up to 24 h. Asking for
 * URLs we may never use (a tab killed at photo 50 of 6,000) would leave
 * thousands of intents nothing will ever fulfil.
 */

import { MAX_STALL_MS } from "./upload-gate";

export interface PresignFileInfo {
  name: string;
  type: string;
  count: number;
  rank: number;
}

export interface PresignedEntry {
  url?: string;
  headUrl?: string;
  key?: string;
}

/**
 * Files per presign request. Well inside the ≤200 the server handles cheaply
 * (one grant insert, two signatures per file), and small enough that a tab
 * dying mid-batch strands at most this many intents.
 */
export const PRESIGN_CHUNK_SIZE = 100;

/**
 * How close to the end of a chunk the queue may get before the next chunk is
 * requested. Larger than the PUT concurrency, so the next URLs are normally
 * waiting by the time a slot frees up.
 */
export const PRESIGN_PREFETCH_WITHIN = 20;

/** Attempts per chunk, including the first. Backoff 1 s, 2 s, 4 s between. */
export const PRESIGN_ATTEMPTS = 4;

/** Pause before a chunk whose attempts all failed is asked again. */
export const PRESIGN_REASK_PAUSE_MS = 15_000;

/**
 * Per-request timeout for the presign call. The shared API axios instance has
 * none, so a request that never answers would otherwise hold its chunk -- and
 * every file waiting on it -- forever.
 */
export const PRESIGN_TIMEOUT_MS = 30_000;

/**
 * Worth retrying: no response at all (offline, dropped, suspended tab), a
 * timeout, rate limiting, or a server error. A 400/401/403/404 is the server
 * answering definitively -- asking again cannot change it.
 */
export function isRetryablePresignError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (!status) return true;
  return status === 408 || status === 429 || status >= 500;
}

export interface PresignQueue {
  /** The URLs for files[index]. Fetches (or waits for) that file's chunk. */
  get(index: number): Promise<PresignedEntry>;
  /**
   * Forget chunks that failed, so the next get() for one of their files asks
   * again. A failed chunk is otherwise remembered, so the rest of its files
   * fail fast instead of each re-running the whole retry ladder.
   */
  reset(): void;
}

export function createPresignQueue(opts: {
  files: PresignFileInfo[];
  request: (files: PresignFileInfo[]) => Promise<PresignedEntry[] | undefined>;
  chunkSize?: number;
  prefetchWithin?: number;
  attempts?: number;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  /**
   * How long files may keep asking again for a chunk that keeps failing,
   * counted from when its files first found it failed. Defaults to the upload
   * gate's total stall; 0 gives up after one round of attempts.
   */
  maxStallMs?: number;
  /** Awaited before each retry, e.g. to hold off while the page is hidden. */
  beforeRetry?: () => Promise<void>;
  onRetry?: (error: unknown, attempt: number, chunk: number) => void;
  /**
   * Called once per chunk the server signed, with each file's global index
   * and storage key. The server records an upload intent per key at this
   * moment, whether or not the file's PUT ever starts. Errors are swallowed.
   */
  onPresigned?: (entries: { index: number; key: string }[]) => void;
}): PresignQueue {
  const size = Math.max(1, opts.chunkSize ?? PRESIGN_CHUNK_SIZE);
  const within = Math.min(opts.prefetchWithin ?? PRESIGN_PREFETCH_WITHIN, size - 1);
  const attempts = Math.max(1, opts.attempts ?? PRESIGN_ATTEMPTS);
  const sleep =
    opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const chunkCount = Math.ceil(opts.files.length / size);
  const now = opts.now ?? (() => Date.now());
  const maxStallMs = Math.max(0, opts.maxStallMs ?? MAX_STALL_MS);

  type Entry = { promise: Promise<PresignedEntry[]>; failed: boolean };
  const chunks = new Map<number, Entry>();
  // When a chunk's files first found it failed. Not when a prefetch failed:
  // a prefetch can fail long before anyone needs it, and that time must not
  // use up the chunk's patience.
  const failingSince = new Map<number, number>();

  const fetchChunk = async (chunk: number): Promise<PresignedEntry[]> => {
    const slice = opts.files.slice(chunk * size, (chunk + 1) * size);
    for (let attempt = 1; ; attempt += 1) {
      try {
        const urls = await opts.request(slice);
        if (!urls || urls.length !== slice.length) {
          throw new Error(
            `Presign returned ${urls?.length ?? 0} urls for ${slice.length} files`,
          );
        }
        try {
          opts.onPresigned?.(
            urls
              .map((entry, i) => ({ index: chunk * size + i, key: entry?.key ?? "" }))
              .filter((entry) => entry.key),
          );
        } catch {
          // Bookkeeping only; it must never fail a presign.
        }
        return urls;
      } catch (error) {
        if (attempt >= attempts || !isRetryablePresignError(error)) throw error;
        opts.onRetry?.(error, attempt, chunk);
        const base = 1000 * 2 ** (attempt - 1);
        await sleep(Math.round(base * (0.85 + Math.random() * 0.3)));
        await opts.beforeRetry?.();
      }
    }
  };

  const start = (chunk: number, fetch: () => Promise<PresignedEntry[]>): Entry => {
    const entry: Entry = { promise: fetch(), failed: false };
    // Marked rather than removed: see reset(). The no-op catch also keeps a
    // prefetch nobody has awaited yet from surfacing as an unhandled rejection.
    entry.promise.catch(() => {
      entry.failed = true;
    });
    chunks.set(chunk, entry);
    return entry;
  };

  const ensure = (chunk: number): Entry =>
    chunks.get(chunk) ?? start(chunk, () => fetchChunk(chunk));

  return {
    async get(index: number): Promise<PresignedEntry> {
      const chunk = Math.floor(index / size);
      let entry = ensure(chunk);
      if (index % size >= size - within && chunk + 1 < chunkCount) {
        void ensure(chunk + 1);
      }
      for (;;) {
        try {
          const urls = await entry.promise;
          failingSince.delete(chunk);
          return urls[index % size];
        } catch (error) {
          if (!isRetryablePresignError(error)) throw error;
          const since = failingSince.get(chunk) ?? now();
          failingSince.set(chunk, since);
          if (now() - since >= maxStallMs) throw error;
          // Every file of the chunk lands here at once; the first replaces
          // the failed entry and the rest wait on that same new request.
          if (chunks.get(chunk) === entry) {
            opts.onRetry?.(error, attempts, chunk);
            start(chunk, async () => {
              await sleep(PRESIGN_REASK_PAUSE_MS);
              await opts.beforeRetry?.();
              return fetchChunk(chunk);
            });
          }
          entry = ensure(chunk);
        }
      }
    },

    reset(): void {
      chunks.forEach((entry, chunk) => {
        if (entry.failed) chunks.delete(chunk);
      });
    },
  };
}
